import React, { useState } from "react";
import styled from "styled-components";
import { Copy, Check } from "react-feather";
import { Button } from "@cinesys/ui/components";
/**
 * Copy-to-clipboard control built on the shared `Button`: icon-only without a `label`, icon + label
 * with one. `hoverOnly` renders it at `opacity: 0` so an ancestor can reveal it with
 * `&:hover .copy-btn { opacity: 1 }`.
 */
const Wrap = styled.span<{ $hoverOnly?: boolean; $hasLabel?: boolean }>`
  display: inline-flex;
  opacity: ${(p) => (p.$hoverOnly ? 0 : 1)};
  transition: opacity 0.12s;

  button span {
    font-size: 12.5px;
  }

  /* Spaces the icon from the label; icon-only buttons skip it so the icon stays centered. */
  ${(p) =>
    p.$hasLabel &&
    `button i {
      margin-left: 4px;
    }`}
`;

export interface CopyButtonProps {
  /** The exact text copied to the clipboard. */
  value: string;
  /** Tooltip while idle — swaps to "Copied!" for a beat after a successful copy. */
  title?: string;
  /** When set, renders as an icon+label button (the label is usually `value` itself) instead of a
   *  bare icon — e.g. the game id, where the id text IS the clickable/copyable control. */
  label?: string;
  /** Starts hidden (`opacity: 0`); an ancestor reveals it on hover via the `copy-btn` class. */
  hoverOnly?: boolean;
  className?: string;
  /** Called after the copy is attempted. */
  onCopy?: () => void;
  /** Overrides `Button`'s own footprint/icon sizing. Default (unset) keeps this component's
   *  usual `iconSize={15}` — Addison: the per-row icon-only UID copy button, at `Button`'s own
   *  default 32px circular footprint, read as way too big sitting inline next to 12-13px row
   *  text, so that call site passes smaller explicit values. */
  size?: number;
  iconSize?: number;
}

const RESET_MS = 1400;

const CopyButton = ({
  value,
  title,
  label,
  hoverOnly,
  className,
  onCopy,
  size,
  iconSize,
}: CopyButtonProps) => {
  const [copied, setCopied] = useState(false);

  const onClick = (e: React.MouseEvent) => {
    // Every call site so far nests this inside something else clickable (a table row that
    // expands on click) — never let the copy action also trigger that.
    e.stopPropagation();
    e.preventDefault();
    navigator.clipboard?.writeText(value).catch(() => {
      // No fallback: a clipboard write can only fail from a permissions/context issue (e.g.
      // non-HTTPS), which a retry can't fix — nothing useful to do beyond not crashing.
    });
    setCopied(true);
    onCopy?.();
    window.setTimeout(() => setCopied(false), RESET_MS);
  };

  return (
    <Wrap
      className={className ? `copy-btn ${className}` : "copy-btn"}
      $hoverOnly={hoverOnly}
      $hasLabel={!!label}
    >
      <Button
        icon={copied ? Check : Copy}
        iconRight={!!label}
        label={label}
        onClick={onClick}
        tooltip={copied ? "Copied!" : title ?? "Copy"}
        type="default"
        size={size}
        iconSize={iconSize ?? 15}
        borderless
      />
    </Wrap>
  );
};

export default CopyButton;
